import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { strings } from "@/lib/strings";
import { cn } from "@/lib/utils";
import { SyncBadge } from "./SyncBadge";
import { OfflineBanner } from "./OfflineBanner";

import logoImg from "@/assets/logo.png";
import { Link } from "react-router-dom";

/** Which navigation entries each role sees (docs/02 permission matrix). */
function useNavItems() {
  const { user, isElevated, canExport, canEditReference, hasRole } = useAuth();
  if (!user) return [];

  const items: Array<{ to: string; label: string }> = [
    { to: "/workbench", label: "Workbench" },
    { to: "/entry", label: "New isolate" },
    { to: "/dashboard", label: "Progress" },
    // Records the laboratory system sent, waiting to be checked (IN-08).
    { to: "/laboratory", label: "From the lab" },
  ];

  // The master view is for roles that may see every row (M-01).
  if (isElevated) items.push({ to: "/isolates", label: "All isolates" });
  else items.push({ to: "/isolates", label: "My isolates" });

  if (isElevated || hasRole("team_lead")) {
    items.push({ to: "/qa-flags", label: "QA flags" });
  }
  if (canExport) {
    items.push({ to: "/antibiogram", label: "Antibiogram" });
    items.push({ to: "/publish", label: "Publish" });
    items.push({ to: "/export", label: "Export" });
  }
  if (canEditReference) {
    items.push({ to: "/reference", label: "Reference data" });
    items.push({ to: "/breakpoints", label: "Breakpoints" });
  }
  if (hasRole("administrator", "data_manager")) {
    items.push({ to: "/users", label: "Users" });
    items.push({ to: "/audit", label: "Audit log" });
  }
  if (hasRole("administrator")) items.push({ to: "/system", label: "System" });

  items.push({ to: "/docs", label: "Docs" });

  return items;
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = useNavItems();

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-dvh bg-slate-50">
      {/* Y-02: a keyboard user can skip the navigation. */}
      <a href="#main" className="sr-only sr-only-focusable">
        Skip to main content
      </a>

      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src={logoImg}
              alt="EKSUTH Logo"
              className="h-8 w-8 rounded-full border border-emerald-600/30 object-contain shadow-sm group-hover:scale-105 transition-transform"
            />
            <span className="text-sm font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
              {strings.app.name}
            </span>
          </Link>

          <div className="hidden sm:flex items-center gap-3 text-xs border-l border-slate-200 pl-3">
            <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium">
              Home
            </Link>
            <span className="text-slate-300">&bull;</span>
            <Link to="/docs" className="text-slate-600 hover:text-slate-900 font-medium">
              Documentation
            </Link>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <SyncBadge />
            {user && (
              <span className="hidden items-center gap-2 text-sm text-slate-600 sm:flex">
                {user.full_name}
                <Badge tone="brand">{user.role.replace(/_/g, " ")}</Badge>
              </span>
            )}
            <NavLink
              to="/settings/notifications"
              className="text-sm text-slate-600 underline hover:text-slate-900"
            >
              Notifications
            </NavLink>
            <Button variant="ghost" size="sm" onClick={signOut}>
              {strings.common.signOut}
            </Button>
          </div>
        </div>

        <nav aria-label="Main" className="mx-auto max-w-7xl overflow-x-auto px-2 pb-1">
          <ul className="flex gap-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "block whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium",
                      isActive
                        ? "bg-brand-50 text-brand-900"
                        : "text-slate-600 hover:bg-slate-100",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <OfflineBanner />

      <main id="main" className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-7xl px-4 pb-8 text-xs text-slate-500">
        <span>
          {import.meta.env.MODE === "production" ? "Production" : "Staging / development"}
          {" · "}
          v{import.meta.env.VITE_APP_VERSION ?? "1.0.0"}
        </span>
      </footer>
    </div>
  );
}
